import { useEffect, useState } from 'react'
import { Check, LoaderCircle, Sparkles, X, Zap } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { fetchBillingOptions } from '@/features/billing/services/billing-service'
import { useMembershipDialogStore } from '@/features/billing/stores/membership-dialog-store'
import { useCreditRechargeDialogStore } from '@/features/billing/stores/credit-recharge-dialog-store'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import type { BillingCreditPack } from '@/features/billing/types'
import { cn } from '@/shared/ui/cn'
import './workspace-upgrade-dialog.css'

function formatPrice(pack: BillingCreditPack) {
  return Number(pack.price).toLocaleString('zh-CN', { maximumFractionDigits: 2 })
}

export function CreditRechargeDialog() {
  const open = useCreditRechargeDialogStore((state) => state.open)
  const close = useCreditRechargeDialogStore((state) => state.closeDialog)
  const openCheckout = useMembershipDialogStore((state) => state.openDialog)
  const credits = useAuthSessionStore((state) => state.session?.membership?.quotas?.details?.generation_credits?.available ?? 0)
  const [packs, setPacks] = useState<BillingCreditPack[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError('')
    void fetchBillingOptions()
      .then((result) => {
        if (!cancelled) setPacks((result.creditPacks ?? []).filter((pack) => pack.quotaMetric === 'generation_credits'))
      })
      .catch((reason) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : '点数包加载失败，请稍后重试。')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [open])

  function choosePack(pack: BillingCreditPack) {
    close()
    openCheckout({ preferredCreditPackCode: pack.packCode, surface: 'workspace' })
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && close()}>
      <DialogContent hideCloseButton className="workspace-upgrade-dialog flex max-h-[calc(100dvh-1.5rem)] max-w-[800px] flex-col overflow-hidden border-[#2a3028] bg-[#101210] p-0 text-[#f5f7f2] shadow-[0_28px_80px_rgba(0,0,0,.62)] sm:rounded-[18px]">
        <div className="shrink-0 border-b border-[#252b24] px-5 py-5 sm:px-7 sm:py-6">
          <div className="flex items-start justify-between gap-5 pr-7">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs font-bold tracking-[.14em] text-[#91e653]"><Sparkles className="size-3.5" />JIAN CREDITS</div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">充值高级生成点数</h2>
              <p className="mt-1.5 max-w-2xl text-sm leading-6 text-[#9aa397]">用于 AI 视频、AI 生图、音乐、配音、数字人与声音复刻等按次消耗的高级能力。</p>
            </div>
            <button type="button" onClick={close} className="-mr-2 -mt-1 rounded-md p-2 text-[#9aa397] transition-colors hover:bg-[#1c221b] hover:text-white" aria-label="关闭点数充值弹窗"><X className="size-5" /></button>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#31402c] bg-[#182016] px-3 py-1.5 text-xs text-[#c7d5c2]"><Zap className="size-3.5 text-[#91e653]" />当前可用：<strong className="text-[#a8f96c]">{credits} 点</strong></div>
        </div>

        <div className="min-h-0 overflow-y-auto px-5 py-5 sm:px-7 sm:py-6">
          {loading ? <div className="grid min-h-56 place-items-center text-sm text-[#a6afa3]"><span className="inline-flex items-center gap-2"><LoaderCircle className="size-4 animate-spin" />正在加载点数包…</span></div> : null}
          {error ? <div className="rounded-xl border border-[#5a322d] bg-[#291714] px-4 py-3 text-sm text-[#ffb4a9]">{error}</div> : null}
          {!loading && !error ? <div className="mx-auto grid max-w-[720px] gap-4 sm:grid-cols-2">
            {packs.map((pack, index) => (
              <article key={pack.packCode} className={cn('workspace-upgrade-dialog__plan flex flex-col rounded-xl border p-5', index === packs.length - 1 ? 'workspace-upgrade-dialog__plan--studio border-[#486453] bg-[#151915]' : 'workspace-upgrade-dialog__plan--creator border-[#2a3028] bg-[#151915]')}>
                <div className="flex items-start justify-between gap-3"><div><h3 className="text-lg font-semibold">{pack.packName}</h3><p className="mt-1 text-xs leading-5 text-[#9ea79b]">{pack.description ?? '用于高级 AI 创作能力'}</p></div>{index === packs.length - 1 ? <span className="rounded-full bg-[#91e653] px-2 py-1 text-[10px] font-semibold text-[#152012]">更划算</span> : null}</div>
                <div className="mt-5 flex items-end gap-2"><span className="text-[2.2rem] font-semibold leading-none tracking-[-.055em] text-[#a8f96c]">{pack.quotaUnits}</span><span className="mb-0.5 text-sm font-medium text-[#c7d5c2]">点</span></div>
                <p className="mt-3 text-2xl font-semibold">¥{formatPrice(pack)}</p>
                <p className="mt-1 text-xs text-[#8d968b]">{pack.expiresDays ? `有效期 ${pack.expiresDays} 天` : '点数长期有效'}</p>
                <button type="button" onClick={() => choosePack(pack)} className={cn('mt-6 inline-flex h-10 items-center justify-center rounded-full px-4 text-sm font-semibold transition-colors', index === packs.length - 1 ? 'bg-[#f5f7f2] text-[#152012] hover:bg-white' : 'border border-[#465044] text-[#f5f7f2] hover:border-[#91e653] hover:text-[#91e653]')}>充值 {pack.quotaUnits} 点</button>
                <p className="mt-4 flex gap-2 border-t border-[#2a3028] pt-3 text-xs leading-5 text-[#bcc4b9]"><Check className="mt-0.5 size-3.5 shrink-0 text-[#91e653]" />支付完成后自动到账，无需刷新页面。</p>
              </article>
            ))}
            {!packs.length ? <p className="col-span-full py-12 text-center text-sm text-[#a6afa3]">当前暂无可购买的高级生成点数包。</p> : null}
          </div> : null}
        </div>
        <p className="border-t border-[#252b24] px-6 py-3 text-center text-xs leading-5 text-[#8d968b]">高级生成点数按实际任务消耗，不随月订阅额度重置。</p>
      </DialogContent>
    </Dialog>
  )
}
